import { useCallback, useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { NudgeRecommendationDetails } from '@atlassian/convo-ai-api';

import { fetchNudgeRecommendations } from './fetchNudgeRecommendations';

type useFetchNudgeRecommendationsReturnType = {
	error?: Error;
	loading: boolean;
	recommendations: NudgeRecommendationDetails | undefined;
};

export const useFetchNudgeRecommendations = (
	triggerId: string,
	pageUrl: string,
	product: string,
): useFetchNudgeRecommendationsReturnType => {
	const [error, setError] = useState<Error | undefined>(undefined);
	const [loading, setLoading] = useState(true);
	const [recommendations, setRecommendations] = useState<NudgeRecommendationDetails | undefined>(
		undefined,
	);

	const makeNudgeRecommendationsRequest = useCallback(async () => {
		if (!fg('aix_nudge_recommendation_migration_enabled')) {
			setLoading(false);
			return;
		}

		// TODO: remove temporary hack when Confluence blog 500 bug is fixed
		const isBlog = pageUrl.includes('/blog/');
		if (isBlog) {
			setLoading(false);
			return;
		}

		if (!product) {
			setLoading(false);
			return;
		}

		try {
			const response = await fetchNudgeRecommendations({ triggerId, pageUrl, product });
			setRecommendations(response?.triggers[0]?.recommendations[0]?.recommendation_details);
			setError(undefined);
		} catch (error: unknown) {
			setRecommendations(undefined);
			setError(error as Error);
		} finally {
			setLoading(false);
		}
	}, [triggerId, pageUrl, product]);

	useEffect(() => {
		void makeNudgeRecommendationsRequest();
	}, [makeNudgeRecommendationsRequest]);

	return useMemo(
		() => ({
			recommendations: loading ? undefined : recommendations,
			loading,
			error,
		}),
		[loading, error, recommendations],
	);
};
